import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Sheet, Stack, Table, Typography,
} from '@mui/joy';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import { APIAdsStrategyRecalc, APIAdsStrategyState, adsErrorText } from '../../../api_ads';
import {
    STRATEGY_LABELS, STRATEGY_PHASE_COLORS, STRATEGY_PHASE_LABELS,
    formatInt, formatMoney,
} from '../adsConstants';


function Row({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
            <Typography level="body-sm" color="neutral">{label}</Typography>
            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{value ?? '—'}</Typography>
        </Stack>
    );
}


/**
 * Состояние автостратегии и история изменения ставки.
 *
 * История показывается всегда, а не по кнопке: когда ставка меняется сама,
 * первый вопрос рекламодателя — «почему стало дороже». Причина каждого
 * изменения на виду снимает этот вопрос без обращения в поддержку.
 */
export default function AdStrategyPanel({ campaign }) {
    const { advertiserToken: token } = useAdvertiser();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);

    const load = useCallback(async () => {
        if (!token || !campaign?.id) return;
        try {
            setLoading(true);
            setError(null);
            const res = await APIAdsStrategyState(token, campaign.id);
            setData(res);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось загрузить состояние стратегии'));
        } finally {
            setLoading(false);
        }
    }, [token, campaign?.id]);

    useEffect(() => { load(); }, [load]);

    const recalc = async () => {
        setBusy(true);
        setNotice(null);
        setError(null);
        try {
            const res = await APIAdsStrategyRecalc(token, campaign.id);
            const result = res?.result || {};
            setNotice(result.changed
                ? `Ставка изменена: ${formatMoney(result.old_bid)} → ${formatMoney(result.new_bid)} ₽`
                : (result.reason || result.skipped || 'Ставка не изменилась'));
            await load();
        } catch (e) {
            setError(adsErrorText(e));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size="sm" /></Stack>;
    }

    if (!data?.is_auto) {
        return (
            <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 0.5 }}>Ручное управление ставками</Typography>
                <Typography level="body-sm" color="neutral">
                    Ставка остаётся такой, какую вы задали в настройках. Чтобы система
                    подбирала её сама под недельный бюджет, выберите автоматическую
                    стратегию во вкладке «Настройки».
                </Typography>
            </Sheet>
        );
    }

    const state = data.state;
    const history = data.history || [];

    return (
        <Box>
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {notice && (
                <Alert color="success" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="success" onClick={() => setNotice(null)}>
                        Скрыть
                    </Button>
                }>{notice}</Alert>
            )}

            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    spacing={1}
                    sx={{ mb: 1.5 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AutoFixHighRoundedIcon color="primary" />
                        <Typography level="title-sm">
                            {STRATEGY_LABELS[campaign.bid_strategy] || campaign.bid_strategy}
                        </Typography>
                        {state?.phase && (
                            <Chip
                                size="sm"
                                variant="soft"
                                color={STRATEGY_PHASE_COLORS[state.phase] || 'neutral'}
                            >
                                {STRATEGY_PHASE_LABELS[state.phase] || state.phase}
                            </Chip>
                        )}
                    </Stack>

                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<RefreshRoundedIcon />}
                        loading={busy}
                        onClick={recalc}
                    >
                        Пересчитать сейчас
                    </Button>
                </Stack>

                {!state ? (
                    <Alert color="neutral" variant="soft" size="sm">
                        Стратегия ещё не запускалась. Первый пересчёт произойдёт
                        в течение часа после старта показов.
                    </Alert>
                ) : (
                    <>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box sx={{ flex: 1 }}>
                                <Row
                                    label="Текущая ставка"
                                    value={`${formatMoney(state.current_bid)} ₽`}
                                />
                                <Row
                                    label="Коридор"
                                    value={`${formatMoney(state.min_bid)} — ${formatMoney(state.max_bid)} ₽`}
                                />
                                <Row label="Пересчётов" value={formatInt(state.iterations)} />
                                <Row
                                    label="Последний пересчёт"
                                    value={state.last_recalc_at
                                        ? format(new Date(state.last_recalc_at), 'd MMM, HH:mm', { locale: ru })
                                        : '—'}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Row
                                    label="Расход за 7 дней"
                                    value={`${formatMoney(state.week_spend)} ₽`}
                                />
                                <Row label="Показы за 7 дней" value={formatInt(state.week_impressions)} />
                                <Row label="Клики за 7 дней" value={formatInt(state.week_clicks)} />
                                <Row
                                    label="Средняя цена клика"
                                    value={`${formatMoney(state.avg_cpc)} ₽`}
                                />
                            </Box>
                        </Stack>

                        {state.last_reason && (
                            <Alert
                                color={state.phase === 'capped' ? 'warning' : 'neutral'}
                                variant="soft"
                                size="sm"
                                sx={{ mt: 1.5 }}
                            >
                                {state.last_reason}
                            </Alert>
                        )}

                        {state.phase === 'learning' && (
                            <Alert color="neutral" variant="soft" size="sm" sx={{ mt: 1 }}>
                                Идёт обучение: пока данных мало, ставка меняется вдвое
                                осторожнее обычного. Дайте кампании набрать статистику.
                            </Alert>
                        )}
                    </>
                )}
            </Sheet>

            <Typography level="title-sm" sx={{ mb: 1 }}>История изменений ставки</Typography>
            {history.length === 0 ? (
                <Sheet variant="soft" sx={{ p: 3, borderRadius: 'md', textAlign: 'center' }}>
                    <Typography level="body-sm" color="neutral">
                        Ставка ещё не менялась
                    </Typography>
                </Sheet>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table sx={{ '--TableCell-paddingY': '9px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 160 }}>Когда</th>
                                <th style={{ width: 170 }}>Было → стало</th>
                                <th>Причина</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((row) => {
                                const up = Number(row.new_bid) > Number(row.old_bid);
                                return (
                                    <tr key={row.id}>
                                        <td>
                                            <Typography level="body-xs">
                                                {row.date_create
                                                    ? format(new Date(row.date_create),
                                                        'd MMM yyyy, HH:mm', { locale: ru })
                                                    : '—'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Stack direction="row" spacing={0.8} alignItems="center">
                                                <Typography level="body-sm">
                                                    {formatMoney(row.old_bid)} ₽
                                                </Typography>
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={up ? 'warning' : 'success'}
                                                >
                                                    {formatMoney(row.new_bid)} ₽
                                                </Chip>
                                            </Stack>
                                        </td>
                                        <td>
                                            <Typography level="body-xs" color="neutral">
                                                {row.reason || '—'}
                                            </Typography>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Sheet>
            )}
        </Box>
    );
}